/**
 * Escuta das URLs que abrem o app — a ponta nativa dos controles da Ilha.
 *
 * Vive em arquivo separado de `focusDeepLink.ts` de propósito: aquele é PURO
 * (parse e build de URL) e é importado por `focusLiveActivity.ts`, que a suíte
 * carrega em toda execução. Um import de plugin lá obrigaria todo teste de
 * payload a mockar `@capacitor/app` para responder a uma pergunta que não é
 * dele.
 *
 * ⚠️ O listener recebe TODA URL que abre o app — inclusive as do OAuth do
 * Google, que já usam `CFBundleURLTypes`. Quem separa é o parser; aqui nada é
 * assumido sobre a origem.
 */
import { App } from "@capacitor/app";
import { isNativeApp } from "../../../lib/platform/native";
import {
  isReplayDuplicate,
  parseFocusDeepLink,
  type AppliedFocusCommand,
  type FocusDeepLinkCommand,
} from "./focusDeepLink";

/* ── Memória do último comando aplicado (BUG-232) ────────────────────────── */

/**
 * Persistida em `localStorage` de propósito: a recriação da WebView (BUG-197)
 * destrói o processo de JS e é exatamente o instante em que o `AppDelegate`
 * reentrega — uma memória de módulo morreria junto e o replay seria aceito
 * como novo. Falha de storage (privado, cota) vira "não sei", e sobra a dedupe
 * por `id` dentro do processo.
 */
const LAST_COMMAND_KEY = "lura.focus.lastCommand";

export function readLastAppliedCommand(): AppliedFocusCommand | null {
  try {
    const raw = localStorage.getItem(LAST_COMMAND_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AppliedFocusCommand>;
    if (typeof parsed.url !== "string" || typeof parsed.atMs !== "number") return null;
    return { url: parsed.url, atMs: parsed.atMs };
  } catch {
    return null;
  }
}

export function rememberAppliedCommand(command: AppliedFocusCommand): void {
  try {
    localStorage.setItem(LAST_COMMAND_KEY, JSON.stringify(command));
  } catch {
    // Storage indisponível: fica só a dedupe por `id` do processo.
  }
}

/**
 * Chama `cb` quando o app é aberto por um controle da Live Activity.
 *
 * Devolve o unsubscribe já PLANO — nunca o handle do plugin. É a mesma regra
 * do `nativeMessaging.ts`: o objeto que o `registerPlugin` devolve é um Proxy
 * que fabrica método para qualquer propriedade, `then` incluído, e atravessar
 * fronteira de promise com ele pendura o `await` para sempre (BUG-154).
 */
export async function nativeOnFocusDeepLink(
  cb: (command: FocusDeepLinkCommand) => void,
): Promise<() => void> {
  if (!isNativeApp()) return () => undefined;
  try {
    const handle = await App.addListener("appUrlOpen", (event) => {
      const url = (event.url ?? "").trim();
      const command = parseFocusDeepLink(url);
      if (!command) return;
      // A porta normal também REGISTRA o que aplicou (BUG-232): é o que deixa
      // a ponte de replay, numa WebView recriada segundos depois, reconhecer
      // este mesmo toque como já feito.
      rememberAppliedCommand({ url, atMs: Date.now() });
      cb(command);
    });
    return () => {
      // `.catch`: `remove()` devolve promise, e rejeitada vazava como
      // unhandled rejection no unmount (BUG-234).
      Promise.resolve(handle.remove()).catch(() => undefined);
    };
  } catch (err) {
    // Mesma regra 1 da Live Activity: o controle é conveniência de mostrador;
    // falhar aqui devolve o app de hoje (o toque no card abre o app e a pessoa
    // age pelo drawer), nunca um app quebrado.
    console.warn("[Lura] Foco: listener de deep link indisponível.", err);
    return () => undefined;
  }
}
/* ── Reentrega nativa depois da recriação da WebView (BUG-204/F8) ────────── */

/** Nome da global que o `AppDelegate` chama. Uma cópia, num lugar só. */
export const FOCUS_COMMAND_GLOBAL = "__luraFocusCommand";

/**
 * Segunda porta do mesmo comando — a que existe porque a primeira morre.
 *
 * ## Por que uma segunda porta
 *
 * O toque num controle da Ilha abre o app com `livra://foco/...`, e nas duas
 * rotas em que a recriação PREVENTIVA age (Home e Perfil, BUG-197) o
 * `didBecomeActive` troca o `rootViewController` logo em seguida. Com ele vão o
 * bridge, a instância do `AppPlugin` e o `retainedEventArguments` — que é
 * propriedade **de instância** no Capacitor (`CAPPlugin.m:82-93`), não do
 * processo. O `appUrlOpen` que chegaria aqui simplesmente deixa de existir, e o
 * sintoma para quem tocou é o pior possível: o app abre e não faz nada.
 *
 * **A recriação NÃO é suspensa para isto** — decisão do fundador: ela é a cura
 * do BUG-197, e trocar um bug por outro foi recusado. O comando atravessa a
 * reconstrução do lado de fora, guardado em `UserDefaults` pelo `AppDelegate`,
 * e volta por aqui.
 *
 * ## O `id` não é enfeite
 *
 * O nativo insiste até o JS confirmar (a WebView nova leva segundos até montar
 * o bundle), então a mesma entrega pode chegar duas vezes se uma confirmação se
 * perder. O `id` é o instante da captura, estável entre retries: aplicar duas
 * vezes um `encerrar` fecharia uma sessão que a pessoa acabou de retomar.
 *
 * A dedupe mora aqui, e não no consumidor, porque o `seq` do
 * `FocusActionRequest` conta PEDIDOS — pausar, retomar e pausar de novo são três
 * toques com dois verbos repetidos, e ele existe justamente para não os
 * colapsar. Ele não pode virar dedupe sem perder a função.
 */
export function installFocusCommandBridge(
  cb: (command: FocusDeepLinkCommand) => void,
): () => void {
  if (!isNativeApp()) return () => undefined;
  const w = window as unknown as Record<string, unknown>;
  let ultimoId: string | null = null;

  const hook = (url: unknown, id: unknown): void => {
    const idStr = typeof id === "string" ? id : null;
    // O nativo só reentrega o que ele mesmo capturou, então uma entrega sem id
    // é sinal de contrato quebrado — melhor ignorar que aplicar às cegas.
    if (!idStr || idStr === ultimoId) return;
    // O `id` É o instante da captura em ms (`String(Int(at))` no Swift).
    const idMs = Number(idStr);
    if (!Number.isFinite(idMs)) return;
    const urlStr = typeof url === "string" ? url.trim() : "";
    const command = parseFocusDeepLink(urlStr);
    if (!command) return;
    /*
     * 🔴 Duplicata do que o processo ANTERIOR já aplicou (BUG-232). A memória
     * de módulo (`ultimoId`) morre com a WebView; esta sobrevive. Sem ela, um
     * `pausar` entregue pelo `appUrlOpen` e reentregue pelo `AppDelegate`
     * numa recriação dentro de 60 s pausava de novo a sessão que a pessoa já
     * tinha retomado pela tela.
     */
    if (isReplayDuplicate(readLastAppliedCommand(), urlStr, idMs)) return;
    ultimoId = idStr;
    rememberAppliedCommand({ url: urlStr, atMs: idMs });
    cb(command);
  };

  w[FOCUS_COMMAND_GLOBAL] = hook;
  return () => {
    // Só remove a PRÓPRIA — o StrictMode monta duas vezes, e apagar a global
    // de outra instância deixaria o app sem porta nenhuma (classe BUG-141).
    if (w[FOCUS_COMMAND_GLOBAL] === hook) delete w[FOCUS_COMMAND_GLOBAL];
  };
}
